/**
 * CalmLearnerDashboard — the P2-lite restructure.
 *
 * Five blocks, top-to-bottom, calm and obvious:
 *
 *   1. Your current focus      → LearnerFrameworkStrip (responsibility %)
 *   2. Upload or review a drive → primary CTA card → /lesson-review
 *   3. Your latest review       → LatestReviewCard (state-aware)
 *   4. Your progress            → LearnerTopDestinations (passport entry)
 *   5. Next lesson / homework   → UpcomingLessonReminderBanner + LearnerHomeworkList
 *
 * NOTE: this replaces the previous ~22-component stack on /learn but
 * does NOT delete those components. Conditional banners that auto-hide
 * when empty (TrackOnboardingCard, IndependentTrackBanner,
 * PendingAIOfferInbox, LearnerLicencePrompt, PassedStoryPrompt) STILL
 * render — they're noise-free when not needed and trust-critical when
 * they ARE needed.
 *
 * Surfaces that are intentionally NOT rendered here (still reachable
 * via direct routes):
 *   • Pre-lesson HowWillYouDriveCard      → /today
 *   • TrainingModeCard                    → /learn-mode
 *   • RoundaboutsLearnCard                → /learn/show-tell
 *   • LearnerHomeCards (the 7-card grid)  → individual routes still live
 *   • SprintOneTools                      → /learn/styles
 *   • LearnerOpeningFlow (check-in flow)  → /today
 *   • RecommendedLessons                  → /learn/lessons
 *   • PasskeyEnrollPanel                  → /settings
 *   • Lesson modules grid                 → /learn/lessons
 *
 * Trust + Continuity rule: every persistent card explains what it is
 * in one line + has a single primary action.
 */
import { Link, useNavigate } from "react-router-dom";
import { Video, ChevronRight, Sparkles, BookOpen } from "lucide-react";

import TrackOnboardingCard from "../components/learner/TrackOnboardingCard";
import StillLookingInstructorBanner from "../components/learner/StillLookingInstructorBanner";
import IndependentTrackBanner from "../components/learner/IndependentTrackBanner";
import PendingAIOfferInbox from "../components/learner/PendingAIOfferInbox";
import LearnerFrameworkStrip from "../components/learner/LearnerFrameworkStrip";
import LearnerTopDestinations from "../components/learner/LearnerTopDestinations";
import UpcomingLessonReminderBanner from "../components/learner/UpcomingLessonReminderBanner";
import LearnerLicencePrompt from "../components/LearnerLicencePrompt";
import PassedStoryPrompt from "../components/learner/PassedStoryPrompt";
import LearnerHomeworkList from "../components/learner/LearnerHomeworkList";
import HomeworkContinuityBridge from "../components/learner/HomeworkContinuityBridge";
import LatestReviewCard from "../components/learner/LatestReviewCard";
import AfterLessonReflectionCard from "../components/learner/AfterLessonReflectionCard";
import CalmRedButtonEntry from "../components/learner/CalmRedButtonEntry";
import { MEDIA_FEATURE_ENABLED } from "../lib/mediaFeatureFlag";
import CalmSupportLevelEntry from "../components/learner/CalmSupportLevelEntry";
import DailyCheckinNudge from "../components/learner/DailyCheckinNudge";
import LearnerPassportContextualCard from "../components/learner/LearnerPassportContextualCard";
import CardErrorBoundary from "../components/CardErrorBoundary";
import V3UploadEntryCard from "../components/V3UploadEntryCard";
import { useAuth } from "../context/AuthContext";

export default function CalmLearnerDashboard({
  showTrackOnboarding,
  isIndependent,
  trackState,
  onTrackAnswered,
  // Pupil L3 Phase 1 Break #4 (Feb 28, 2026) — when the engine's
  // `where_you_are` slot is firing above, suppress the legacy
  // framework strip's Card 1 ("Current focus") so the learner
  // never reads the same primary orientation twice. Default
  // `false` so direct mounts of this page outside LearnerStateHome
  // still get the full strip.
  suppressPrimaryOrientation = false,
  // Phase 5 — Operational Continuity Convergence v2 (Feb 28, 2026).
  // When the engine's `from_your_last_lesson` slot is populated
  // above, the HomeworkContinuityBridge defers its "Last time:"
  // row silently — engine slot owns the backward question. Default
  // false so direct mounts of this page outside LearnerStateHome
  // get the original full bridge.
  suppressBridgeLastTime = false,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div data-testid="learner-hub-calm-v1" className="space-y-5">
      {/* §23 Step 15 — fossils unmounted and deleted:
            • <AuthorityStatement>      → now a `authority_statement` field on /home
            • <ContextualGuidanceCard>  → now the engine's `right_now` slot
          Both source files removed from the codebase. Their roles are
          owned end-to-end by <LearnerStateHome>. Calm-baseline silence
          when no rule fires is the new default (§18). */}

      {/* ── After-lesson micro-reflection — Phase 2 of the Reflection
          System (May 14, 2026). Replaces the static
          questionnaire (retired in §23 Step 7). One prompt → one
          response → one supportive follow-up. Silent by default. ── */}
      <AfterLessonReflectionCard />

      {/* Activation Phase 1 #1 (Feb 17, 2026) — gentle Red Button
          surfacing per AP-5 operator decision. ONE quiet inline
          link; bottom sheet mounts the existing RedButtonCard. No
          new backend; calm-baseline silence when learner has
          already activated today. */}
      <CalmRedButtonEntry />
      <CalmSupportLevelEntry />

      {/* A2 (Feb 27, 2026 exposure batch) — daily check-in
          contextual nudge. Self-hides when completed_today=true OR
          dismissed for the session. Single quiet line; no operational
          weight per Learner Doctrine (one meaningful next step). */}
      <DailyCheckinNudge />

      {/* A1 (Feb 27, 2026 exposure batch) — Passport contextual
          card. Self-hides unless a lesson stamp arrived in the
          last 7 days. Routes to /passport (which was previously
          invisible to its rightful owner). Pure exposure. */}
      <LearnerPassportContextualCard />

      {/* ── Always-on conditional banners (auto-hide when not needed) ── */}
      {showTrackOnboarding && (
        <TrackOnboardingCard onAnswered={onTrackAnswered} />
      )}
      {/* May 14, 2026 — 7-day "still looking?" recovery. Renders only
          when /api/tracks/me returns find_instructor_pending: true. */}
      <StillLookingInstructorBanner
        trackState={trackState}
        onChanged={onTrackAnswered}
      />
      {isIndependent && <IndependentTrackBanner />}
      <PendingAIOfferInbox />
      <LearnerLicencePrompt />
      <PassedStoryPrompt />

      {/* ───────────── Card 1: Where I am now ───────────── */}
      <CardErrorBoundary region="learner-current-focus" label="Your current focus">
        <section data-testid="learner-card-current-focus" aria-labelledby="card-1-title">
          <h2 id="card-1-title" className="sr-only">Your current focus</h2>
          <LearnerFrameworkStrip
            suppressPrimaryOrientation={suppressPrimaryOrientation}
          />
        </section>
      </CardErrorBoundary>

      {/* ───────────── Card 2: Upload or review a drive ─────────────
          Hidden under media quarantine — no dead button into /lesson-review. */}
      {MEDIA_FEATURE_ENABLED && (
      <button
        data-testid="learner-card-upload-review"
        onClick={() => navigate("/lesson-review")}
        className="w-full text-left rounded-2xl border-2 border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 p-5 hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Video className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-600 dark:text-indigo-300">
              Upload or review a drive
            </p>
            <p className="text-base font-extrabold text-slate-900 dark:text-white mt-1">
              Send a clip to review
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
              Goes straight to secure storage. We&apos;ll let you know when it&apos;s ready.
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-2" />
        </div>
      </button>
      )}

      {/* ── V3 upload entry (Stage C.1, May 26, 2026) ─────────────
          Calm persona-aware doorway into the thin UploadShell.
          /learner/upload itself defers every byte to /api/v3/media/*.
          Card auto-hides for any role outside the persona table.
          Renders alongside the (currently quarantined) legacy entry
          so when V2 returns there is no conflict — they target
          different routes and different infrastructure. */}
      <CardErrorBoundary region="learner-v3-upload-entry" label="V3 upload entry">
        <V3UploadEntryCard user={user} />
      </CardErrorBoundary>

      {/* ───────────── Card 3: Your latest review ───────────── */}
      <CardErrorBoundary region="learner-latest-review" label="Your latest review">
        <section data-testid="learner-card-latest-review" aria-labelledby="card-3-title">
          <h2 id="card-3-title" className="sr-only">Your latest review</h2>
          <LatestReviewCard />
        </section>
      </CardErrorBoundary>

      {/* ───────────── Card 4: Your progress ───────────── */}
      <CardErrorBoundary region="learner-progress" label="Your progress">
        <section data-testid="learner-card-progress" aria-labelledby="card-4-title">
          <h2 id="card-4-title" className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400 mb-2 px-1">
            Your progress
          </h2>
          <LearnerTopDestinations />
        </section>
      </CardErrorBoundary>

      {/* ───────────── Card 5: Next lesson / homework ───────────── */}
      <CardErrorBoundary region="learner-next" label="Your next lesson and homework">
        <section data-testid="learner-card-next" aria-labelledby="card-5-title">
          <h2 id="card-5-title" className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400 mb-2 px-1">
            Next lesson &amp; homework
          </h2>
          <div className="space-y-3">
            {/* Phase 5 deference: the HomeworkContinuityBridge below
                owns the "Last time → This time" psychological
                question. The banner therefore suppresses its own
                continuity echo here and stays a calm calendar
                reminder. Doctrine: "If two surfaces answer the same
                psychological question, one of them should defer
                silently." */}
            <UpcomingLessonReminderBanner suppressContinuityLine />
            <HomeworkContinuityBridge suppressLastTime={suppressBridgeLastTime} />
            <LearnerHomeworkList />
          </div>
        </section>
      </CardErrorBoundary>

      {/* ───────────── Footer link — learning topics surface ─────────────
          Routes to /learn/hub (24-topic catalogue, Canonical §12). Booking
          hub at /learn/lessons stays distinct and is reachable from /learn
          and the booking flows. Doctrine Pass 1 §3.2.c — sub-line now
          matches the destination's content; Sparkles swapped for BookOpen
          to match the green Learning intent circle on /learn. */}
      <Link
        to="/learn/hub"
        data-testid="learner-lessons-deep-link"
        className="w-full flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 dark:text-white">Learning environment</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Topics and reading list
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
      </Link>
    </div>
  );
}
